import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import { Text, Flex, Box } from '@atlaskit/primitives/compiled';
import type { AvatarProps } from '@atlaskit/avatar-group';
import ProgressBar from '@atlaskit/progress-bar';

import { ApprovalStatusButton } from './ApprovalStatusButton.tsx';

const styles = cssMap({
	fullWidth: {
		width: '100%',
	},
	progressRow: {
		width: '192px',
	},
	textContainer: {
		flexShrink: 0,
		textAlign: 'start',
	},
	progressBarContainer: {
		flexGrow: 1,
		minWidth: '0px',
	},
});

const i18n = defineMessages({
	inProgressApprovalTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.in-progress-title.non-final',
		defaultMessage: 'Approval in progress',
		description: 'Title for indicating the status of the approval process',
	},
	inProgressApprovalDescription: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.in-progress-description.non-final',
		defaultMessage: '{approvalCount}/{approverCount} approved',
		description:
			'Description for indicating how many approvers have approved the content. approvalCount is the number of approvals received, approverCount is the total number of approvals required.',
	},
});

export interface InProgressApprovalButtonProps {
	onClick?: () => void;
	approvalCount: number;
	approverCount: number;
	showProgressBar?: boolean;
	avatarProps?: AvatarProps[];
}

export const InProgressApprovalButton = ({
	onClick,
	showProgressBar,
	approvalCount,
	approverCount,
	avatarProps,
}: InProgressApprovalButtonProps) => {
	const { formatMessage } = useIntl();

	return (
		<ApprovalStatusButton onClick={onClick} avatarProps={avatarProps}>
			<Flex direction="column" alignItems="start" gap="space.025" xcss={styles.fullWidth}>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(i18n.inProgressApprovalTitle)}
				</Text>
				<Flex direction="row" gap="space.100" alignItems="center" xcss={styles.progressRow}>
					<Box xcss={styles.textContainer}>
						<Text size="medium">
							{formatMessage(i18n.inProgressApprovalDescription, { approvalCount, approverCount })}
						</Text>
					</Box>
					{showProgressBar && (
						<Box xcss={styles.progressBarContainer}>
							<ProgressBar
								appearance="success"
								value={approvalCount / approverCount}
								ariaLabel={formatMessage(i18n.inProgressApprovalDescription, {
									approvalCount,
									approverCount,
								})}
							/>
						</Box>
					)}
				</Flex>
			</Flex>
		</ApprovalStatusButton>
	);
};
